import type {
  IFilterData,
  IHeader,
  IQuickFilter,
} from "../../../components/Organismos/Table/FilteredTable.types";
import {
  ACCESS_LOGIN_FLOW_ACTIONS,
  AccessActionType,
  AccessActionTypeEnum,
} from "../../../Enums/LogsEnum";
import {
  normalizeRoleToKey,
  Roles,
  RoleTypes,
} from "../../../Enums/UserEnums";
import { defaultAuditLogDateQueryClauses } from "../../../utils/auditLogDateFilters";
import { createAuditLogDateFilterPanel } from "../../../utils/auditLogDateFilterPanel";
import { formatAuditTimestamp, formatAuditDateOnly } from "./auditLogTableConfig";

export type FirmLogMode =
  | "modificationLog"
  | "dataAdminLogin"
  | "professionalLogin";

export type FirmLogOfficeScope =
  | { kind: "parent" }
  | { kind: "office"; officeId: string }
  | { kind: "standalone"; officeId: string };

export type BuildFirmLogFilterOptions = {
  includeDefaultDateClauses?: boolean;
  officeScope?: FirmLogOfficeScope;
};

function buildModificationLogOfficeFilter(
  officeScope?: FirmLogOfficeScope,
): Record<string, unknown> | undefined {
  if (!officeScope || officeScope.kind === "parent") {
    return {
      officeId: { comparisonType: "MISSING_OR_NULL_OR_EMPTY" },
    };
  }

  if (officeScope.kind === "office") {
    return {
      officeId: {
        value: officeScope.officeId,
        comparisonType: "EQUAL",
      },
    };
  }

  return {
    officeId: {
      value: officeScope.officeId,
      comparisonType: "MISSING_OR_EQUAL",
    },
  };
}

export const FIRM_LOG_QUICK_FILTERS: IQuickFilter[] = [
  { key: "modificationLog", label: "Modification Log" },
  { key: "dataAdminLogin", label: "Data Admin Log" },
  { key: "professionalLogin", label: "Professional Login" },
];

export function parseCountFromResponseBody(body: unknown): number {
  if (typeof body === "number" && Number.isFinite(body)) {
    return body;
  }
  if (Array.isArray(body) && body.length > 0) {
    const value = body[0];
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof body === "string" && body.trim() !== "") {
    const parsed = Number(body);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function buildFirmLogCountFilter(
  firmId: string,
  mode: FirmLogMode,
  options?: BuildFirmLogFilterOptions,
): Record<string, unknown> {
  return buildFirmLogPersistentFilter(firmId, mode, {
    ...options,
    includeDefaultDateClauses: true,
  });
}

export function buildFirmLogPersistentFilter(
  firmId: string,
  mode: FirmLogMode,
  options: BuildFirmLogFilterOptions | boolean = false,
): Record<string, unknown> {
  const normalizedOptions: BuildFirmLogFilterOptions =
    typeof options === "boolean"
      ? { includeDefaultDateClauses: options }
      : options;
  const includeDefaultDateClauses =
    normalizedOptions.includeDefaultDateClauses ?? false;
  const dateClauses = includeDefaultDateClauses
    ? defaultAuditLogDateQueryClauses()
    : undefined;
  const filter: Record<string, unknown> = {
    firmId: { value: firmId, comparisonType: "EQUAL" },
    ...(dateClauses ?? {}),
  };

  if (mode === "modificationLog") {
    Object.assign(
      filter,
      buildModificationLogOfficeFilter(normalizedOptions.officeScope),
    );
  }

  if (mode === "dataAdminLogin") {
    filter.role = { value: "dataAdmin", comparisonType: "EQUAL" };
    filter.action = {
      value: [...ACCESS_LOGIN_FLOW_ACTIONS],
      comparisonType: "IN",
    };
  } else if (mode === "professionalLogin") {
    filter.role = { value: "member", comparisonType: "EQUAL" };
    filter.action = {
      value: [...ACCESS_LOGIN_FLOW_ACTIONS],
      comparisonType: "IN",
    };
  }

  return filter;
}

export function getFirmLogEndpoint(mode: FirmLogMode): string {
  return mode === "modificationLog" ? "/auditLog" : "/accessLog";
}

export function getFirmLogExcelFilename(
  firmName: string | undefined,
  mode: FirmLogMode,
): string {
  const excelBase = firmName
    ? `${firmName.replace(/[^\w-]+/g, "_")}-logs`
    : "firm-logs";
  return `${excelBase}-${mode}.xlsx`;
}

export function isFirmLogModeKey(key: string): key is FirmLogMode {
  return (
    key === "modificationLog" ||
    key === "dataAdminLogin" ||
    key === "professionalLogin"
  );
}

export function formatRoleFallback(role: string): string {
  const spaced = role.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatAccessRole(role?: string): string {
  if (!role) {
    return "—";
  }
  const key = normalizeRoleToKey(role) as RoleTypes;
  return Roles[key] ?? formatRoleFallback(role);
}

export function buildAccessLogColumnsDef(): IHeader[] {
  return [
    { id: "timestamp", header: "Date", width: 170 },
    { id: "action", header: "Action", width: 160 },
    { id: "userName", header: "User", width: 120 },
    { id: "email", header: "Email", width: 200 },
    { id: "role", header: "Role", width: 160, enableSorting: false },
    { id: "success", header: "Success", width: 90 },
    { id: "reason", header: "Reason", width: 200 },
  ];
}

export function buildAccessLogFiltersData(nestedInModal: boolean): IFilterData[] {
  return [
    {
      name: "Action",
      fieldName: "action",
      comparison: "EQUAL",
      disabledWrite: true,
      objectData: ACCESS_LOGIN_FLOW_ACTIONS.map((key) => ({
        id: key,
        name: AccessActionType[key as AccessActionTypeEnum] ?? key,
      })),
    },
    createAuditLogDateFilterPanel({
      nestedInModal,
      dialogTitle: "Access log date",
    }),
  ];
}

export function buildFirmAccessLogFiltersData(
  nestedInModal: boolean,
  dialogTitle: string,
): IFilterData[] {
  return [
    createAuditLogDateFilterPanel({
      nestedInModal,
      pickerVariant: "firmLogs",
      dialogTitle,
    }),
  ];
}

export function buildFirmAccessLogColumnsDef(): IHeader[] {
  return [
    { id: "timestamp", header: "Date", width: 120 },
    { id: "action", header: "Action", width: 160 },
    { id: "userName", header: "User", width: 140 },
    { id: "email", header: "Email", width: 200 },
    { id: "role", header: "Role", width: 140, enableSorting: false },
    { id: "success", header: "Success", width: 90 },
    { id: "reason", header: "Reason", width: 200 },
  ];
}

export function formatAccessLogRows(rows: any[]): any[] {
  return rows.map((row) => ({
    ...row,
    timestamp: formatAuditTimestamp(row.timestamp),
    action:
      AccessActionType[row.action as AccessActionTypeEnum] ?? row.action ?? "—",
    role: formatAccessRole(row.role),
    success: row.success === true ? "Yes" : row.success === false ? "No" : "—",
    reason: row.reason || "—",
    userName: row.userName || "—",
    email: row.email || "—",
  }));
}

export function formatFirmAccessLogRows(rows: any[]): any[] {
  return rows.map((row) => ({
    ...formatAccessLogRows([row])[0],
    timestamp: formatAuditDateOnly(row.timestamp),
  }));
}

export async function fetchFirmLogCount(
  get: (url: string) => Promise<{ data: unknown }>,
  endpoint: string,
  filter: Record<string, unknown>,
): Promise<number> {
  const params = new URLSearchParams();
  params.append("filter", JSON.stringify(filter));
  params.append("count", "true");
  const response = await get(`${endpoint}?${params.toString()}`);
  return parseCountFromResponseBody(response.data);
}
